import React from "react";
import InputField from "../FormFields/InputField";
import SelectField from "../FormFields/SelectField";
import RadioGroupField from "../FormFields/RadioGroupField";
import CheckboxGroupField from "../FormFields/CheckboxGroupField";
import SliderField from "../FormFields/SliderField";
import {
  bloqcibeApi,
  useUploadSignatureMutation,
} from "../../../store/slices/apiSlice";
import { useEffect, useState, useMemo } from "react";
import PhoneInputField from "../FormFields/PhoneInputField";
import DatePickerField from "../FormFields/DatePickerField";
import YearPickerField from "../FormFields/YearPickerField";
import DateTimePickerField from "../FormFields/DateTimePickerField";
import SignatureField from "../FormFields/SignatureField";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import CheckboxField from "../FormFields/CheckboxField";
import DataLabelField from "../FormFields/DataLabelField";
import moment from "moment";
import { Box, Chip, Grid, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import HtmlContentField from '../FormFields/HtmlContentField';
import LabelField from '../FormFields/LabelField';
import ButtonField from "../FormFields/ButtonField";
import UploadDocumentField from "../FormFields/UploadDocumentField";
import AudioField from "../FormFields/AudioField";
import TimePickerField from "../FormFields/TimePickerField";
import { getIn } from "formik";
import MultiSelectField from "../FormFields/MultiSelectField";
import SelectSearchField from "../FormFields/SelectSearchField";
import CustomButton from "../../../components/@extended/CustomButton";
import ColourSliderField from "../FormFields/ColourSliderField";
import ResourceLinkField from "../FormFields/ResourceLinkField";
import { useResponsive } from "../../../hooks/ResponsiveProvider";
import VideoEmbedField from "../FormFields/VideoEmbedField";
import { checkFieldVisibility } from '../utils/dependsOnValidationService';
import QuizIcon from '@mui/icons-material/Quiz';
import { getStatusColor } from '../../util';

const DynamicFormField = (props) => {
  const {
    _config,
    parentKey,
    setFieldValue,
    values,
    siteInitiationMasterId,
    touched,
    readOnly,
    errors,
    sectionKey,
    field,
    removeCustomField,
    userSiteTrialRole,
    hasSignatureAccess = true,
    fieldQueries,
    queriesByFieldKey,
  } = props;
  const [optionValues, setOptionValues] = useState([]);
  const { isSmallScreen } = useResponsive();
  const theme = useTheme();
  const [getDynamicDataValues] =
    bloqcibeApi.endpoints.getDynamicDataValues.useLazyQuery();

  // Helper function to get queries for a child field
  const getChildFieldQueries = (childFieldKey) => {
    return queriesByFieldKey?.[childFieldKey] || [];
  };

  // Helper function to get open queries count for a child field
  const getOpenQueriesCount = (childFieldKey) => {
    const queries = getChildFieldQueries(childFieldKey);
    return queries.filter((q) => q.status === 'OPEN' || q.status === 'REOPENED').length;
  };

  // Helper function to check if child field has any queries
  const childFieldHasQueries = (childFieldKey) => {
    return getChildFieldQueries(childFieldKey).length > 0;
  };

  // Get query status counts for a child field
  const getQueryStatusCounts = (childFieldKey) => {
    const queries = getChildFieldQueries(childFieldKey);
    if (!queries || queries.length === 0) return { OPEN: 0, RESOLVED: 0, VERIFIED: 0, REOPENED: 0, total: 0 };
    
    const counts = { OPEN: 0, RESOLVED: 0, VERIFIED: 0, REOPENED: 0, total: 0 };
    queries.forEach((query) => {
      if (counts.hasOwnProperty(query.status)) {
        counts[query.status]++;
      }
      counts.total++;
    });
    return counts;
  };

  // Get status label for display
  const getStatusLabel = (status) => {
    switch (status) {
      case 'OPEN': return 'Open';
      case 'RESOLVED': return 'Resolved';
      case 'VERIFIED': return 'Verified';
      case 'REOPENED': return 'Reopened';
      default: return status;
    }
  };

  // Render query indicator for a child field
  const renderQueryIndicator = (childFieldKey) => {
    const statusCounts = getQueryStatusCounts(childFieldKey);
    if (statusCounts.total === 0) return null;

    console.log('statusCounts', childFieldKey, statusCounts);

    return (
      <Tooltip
        title={
          <Box sx={{ p: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
              Queries ({statusCounts.total})
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
              {['OPEN', 'REOPENED', 'RESOLVED', 'VERIFIED'].map(
                (status) =>
                  statusCounts[status] > 0 && (
                    <Chip
                      key={status}
                      label={`${getStatusLabel(status)}: ${statusCounts[status]}`}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: '0.65rem',
                        fontWeight: 500,
                        backgroundColor: getStatusColor(status).bg,
                        color: getStatusColor(status).text,
                        '& .MuiChip-label': {
                          paddingX: 0.75,
                        },
                      }}
                    />
                  )
              )}
            </Stack>
          </Box>
        }
        arrow
        placement="top"
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            position: 'absolute',
            top: 4,
            right: 4,
            zIndex: 1,
          }}
        >
          <QuizIcon
            sx={{
              fontSize: 16,
              color:
                statusCounts.OPEN > 0 || statusCounts.REOPENED > 0
                  ? getStatusColor('OPEN').text
                  : statusCounts.RESOLVED > 0
                  ? getStatusColor('RESOLVED').text
                  : getStatusColor('VERIFIED').text,
            }}
          />
        </Box>
      </Tooltip>
    );
  };

  useEffect(() => {
    (async () => {
      if (field && field.options && field.options.endpoint) {
        const result = await getDynamicDataValues({
          url: field.options.endpoint,
        });
        if (result.data && result.data.length > 0) {
          setOptionValues(result.data.map((_obj) => _obj.title));
        }
      }
    })();
  }, [field]);
  return (
    <Box
      sx={{
        columnGap: 2,
        border: field.bordered ? `1.5px solid ${theme.palette.grey.A400}` : '',
        borderRadius: field.bordered ? '25px' : '',
        padding: field.bordered ? 3 : 0,
        height: field.type !== 'REMOVE_FIELD_BUTTON' ? 'auto' : '100%',
      }}
    >
      {field.type == 'DATA_LABEL' && (() => {
        const { key: fieldKey, ...fieldRest } = field;
        return <DataLabelField {...props} {...fieldRest} fieldKey={fieldKey} />;
      })()}
      {field.type == 'VIDEO_LINK' && (() => {
        const { key: fieldKey, ...fieldRest } = field;
        return <ResourceLinkField _key={fieldKey} {...props} {...fieldRest} />;
      })()}
      {field.type == 'EMBED_VIDEO' && (() => {
        const { key: fieldKey, ...fieldRest } = field;
        return <VideoEmbedField _key={fieldKey} {...props} {...fieldRest} />;
      })()}
      {field.type == 'DOCUMENT_LINK' && (() => {
        const { key: fieldKey, ...fieldRest } = field;
        return <ResourceLinkField _key={fieldKey} {...props} {...fieldRest} />;
      })()}
      {field.type == 'LABEL' && (() => {
        const { key: fieldKey, ...fieldRest } = field;
        return <LabelField {...props} {...fieldRest} fieldKey={fieldKey} />;
      })()}
      {field.type == 'DATA_HTML' && (() => {
        const { key: fieldKey, ...fieldRest } = field;
        return <HtmlContentField {...props} _key={fieldKey} {...fieldRest} />;
      })()}
      {field.type == 'BUTTON' && !props?.openOTP && (() => {
        const { key: fieldKey, ...fieldRest } = field;
        return <ButtonField {...props} _key={fieldKey} {...fieldRest} />;
      })()}
      {field.type == 'INLINE_TEXT_FIELD' && field.subtype != 'DATE' && (
        <>
          {(field.role || field.showDate) && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: 2,
              }}
            >
              {field.role && <Typography variant="subtitle1">{field.role}</Typography>}
              {field.showDate && (
                <Typography variant="subtitle1">
                  {parentKey ? values[parentKey][field.currentDateKey] : values[field.currentDateKey]}
                </Typography>
              )}
            </Box>
          )}
          <InputField
            name={parentKey ? `${parentKey}.${field.key}` : field.key}
            label={field.label}
            required={field.required}
            disabled={readOnly ? readOnly : field.disabled}
            justifycontent={field.justifycontent}
            type={field.subtype}
            placeholder={field.placeholder}
            fullWidth
            helperLabel={field.helperLabel}
            unit={field.unit}
            info={field.info}
            valueKey={field.valueKey}
            fieldKey={field.key}
            multiline={field.multiline}
            step={field.step}
            fieldConfig={field}
            requiredDepends={field?.requiredDepends}
            requiredValidationValue={field?.requiredValidationValue}
            disabledDepends={field?.disabledDepends}
            disabledValidationValue={field?.disabledValidationValue}
            requiredDependOnFields={field?.requiredDependOnFields}
            disabledDependsOnFields={field?.disabledDependsOnFields}
            labelFontWeight={field?.labelFontWeight}
            userSiteTrialRole={userSiteTrialRole}
          />
        </>
      )}
      {field.type == 'DROP_DOWN' && (
        <SelectField
          name={parentKey ? `${parentKey}.${field.key}` : field.key}
          data={field.options?.endpoint ? optionValues : field.options?.values}
          disabled={readOnly ? readOnly : field.disabled}
          disabledDepends={field?.disabledDepends}
          disabledValidationValue={field?.disabledValidationValue}
          required={field.required}
          requiredDepends={field?.requiredDepends}
          requiredValidationValue={field?.requiredValidationValue}
          requiredDependOnFields={field?.requiredDependOnFields}
          disabledDependsOnFields={field?.disabledDependsOnFields}
          placeholder={field?.placeholder}
          justifycontent={field.justifycontent}
          label={field.label}
          fieldKey={field.key}
          valueKey={field.valueKey}
          fieldConfig={field}
          enableOther={field?.enableOther}
          fullWidth
          labelFontWeight={field?.labelFontWeight}
        />
      )}
      {field.type == 'MULTISELECT_DROP_DOWN' && (
        <MultiSelectField
          name={parentKey ? `${parentKey}.${field.key}` : field.key}
          data={field.options?.endpoint ? optionValues : field.options?.values}
          disabled={readOnly ? readOnly : field.disabled}
          disabledDepends={field?.disabledDepends}
          disabledValidationValue={field?.disabledValidationValue}
          requiredDependOnFields={field?.requiredDependOnFields}
          disabledDependsOnFields={field?.disabledDependsOnFields}
          required={field.required}
          requiredDepends={field?.requiredDepends}
          requiredValidationValue={field?.requiredValidationValue}
          justifycontent={field.justifycontent}
          label={field.label}
          fieldKey={field.key}
          valueKey={field.valueKey}
          enableOther={field?.enableOther}
          fullWidth
          labelFontWeight={field?.labelFontWeight}
        />
      )}
      {field.type == 'SEARCH_DROP_DOWN' && (
        <SelectSearchField
          name={parentKey ? `${parentKey}.${field.key}` : field.key}
          data={field.options?.endpoint ? optionValues : field.options?.values}
          disabled={readOnly ? readOnly : field.disabled}
          required={field.required}
          justifycontent={field.justifycontent}
          label={field.label}
          fieldKey={field.key}
          valueKey={field.valueKey}
          fullWidth
          multiple={field.multivalue}
          {...props}
        />
      )}
      {field.type == 'FILE_UPLOAD' && (
        <UploadDocumentField
          name={parentKey ? `${parentKey}.${field.key}` : field.key}
          required={field.required}
          justifycontent={field.justifycontent}
          label={field.label}
          supportedFormats={field.supportedFormats}
          fileSize={field.fileSize}
          fieldKey={field.key}
          parentKey={parentKey}
          fullWidth
          {...props}
          disabled={readOnly || field.disabled}
          labelFontWeight={field?.labelFontWeight}
        />
      )}
      {field.type == 'RADIO_BUTTONS' && (
        <RadioGroupField
          name={parentKey ? `${parentKey}.${field.key}` : field.key}
          fieldKey={field.key}
          options={field.options.values}
          optionSize={field.optionSize}
          required={field.required}
          requiredDepends={field?.requiredDepends}
          requiredValidationValue={field?.requiredValidationValue}
          disabledDepends={field?.disabledDepends}
          disabledValidationValue={field?.disabledValidationValue}
          requiredDependOnFields={field?.requiredDependOnFields}
          disabledDependsOnFields={field?.disabledDependsOnFields}
          infoText={field.infoText}
          verticalAlignCenter={field.verticalAlignCenter}
          disabled={readOnly || field.disabled}
          label={field.label}
          fieldConfig={field}
          labelFontWeight={field?.labelFontWeight}
          setFieldValue={setFieldValue}
        />
      )}
      {field.type == 'AUDIO' && (
        <AudioField
          name={parentKey ? `${parentKey}.${field.key}` : field.key}
          disabled={readOnly || field.disabled}
          audioText={field.audioText}
          labelFontWeight={field?.labelFontWeight}
        />
      )}
      {field.type == 'CHECK_BOX' && (
        <CheckboxField
          name={parentKey ? `${parentKey}.${field.key}` : field.key}
          disabled={readOnly || field.disabled}
          disabledDepends={field?.disabledDepends}
          disabledValidationValue={field?.disabledValidationValue}
          disabledDependsOnFields={field?.disabledDependsOnFields}
          label={field.label}
          labelFontWeight={field?.labelFontWeight}
        />
      )}
      {field.type == 'CHECK_BOX_GROUP' && (
        <CheckboxGroupField
          name={parentKey ? `${parentKey}.${field.key}` : field.key}
          justifycontent={field.justifycontent}
          disabled={readOnly || field.disabled}
          label={field.label}
          disabledDepends={field?.disabledDepends}
          disabledValidationValue={field?.disabledValidationValue}
          disabledDependsOnFields={field?.disabledDependsOnFields}
          options={field.options.values}
          labelFontWeight={field?.labelFontWeight}
        />
      )}
      {field.type == 'SLIDER' && (
        <SliderField
          name={parentKey ? `${parentKey}.${field.key}` : field.key}
          justifycontent={field.justifycontent}
          label={field.label}
          disabled={readOnly || field.disabled}
          valueLabelDisplay={field.valueLabelDisplay ? field.valueLabelDisplay : 'off'}
          {...field.defaultProps}
          labelFontWeight={field?.labelFontWeight}
        />
      )}
      {field.type == 'COLOUR_SLIDER' && (
        <ColourSliderField
          name={parentKey ? `${parentKey}.${field.key}` : field.key}
          justifycontent={field.justifycontent}
          label={field.label}
          disabled={readOnly || field.disabled}
          startLabel={field.startLabel}
          endLabel={field.endLabel}
          valueLabelDisplay={field.valueLabelDisplay ? field.valueLabelDisplay : 'off'}
          {...field.defaultProps}
          labelFontWeight={field?.labelFontWeight}
        />
      )}
      {field.type == 'PHONE_INPUT' && (
        <PhoneInputField
          name={parentKey ? `${parentKey}.${field.key}` : field.key}
          label={field.label}
          placeholder={field.placeholder}
          touched={touched}
          required={field.required}
          errors={errors}
          setFieldValue={setFieldValue}
          {...field.defaultProps}
          labelFontWeight={field?.labelFontWeight}
        />
      )}
      {field.type == 'INLINE_TEXT_FIELD' && field.subtype == 'DATE' && (
        <>
          <DatePickerField
            label={field.label}
            fieldConfig={field}
            required={field.required}
            requiredDepends={field?.requiredDepends}
            requiredValidationValue={field?.requiredValidationValue}
            disabledDepends={field?.disabledDepends}
            disabledValidationValue={field?.disabledValidationValue}
            requiredDependOnFields={field?.requiredDependOnFields}
            disabledDependsOnFields={field?.disabledDependsOnFields}
            justifycontent={field.justifycontent}
            name={parentKey ? `${parentKey}.${field.key}` : field.key}
            setFieldValue={setFieldValue}
            disabled={readOnly ? readOnly : field.disabled}
            disableFutureDates={field.disableFutureDates}
            disablePastDates={field.disablePastDates}
            valueKey={field.valueKey}
            fieldKey={field.key}
            labelFontWeight={field?.labelFontWeight}
          />
        </>
      )}
      {field.type == 'YEAR_PICKER' && (
        <>
          <YearPickerField
            label={field.label}
            fieldConfig={field}
            required={field.required}
            requiredDepends={field?.requiredDepends}
            requiredValidationValue={field?.requiredValidationValue}
            disabledDepends={field?.disabledDepends}
            disabledValidationValue={field?.disabledValidationValue}
            requiredDependOnFields={field?.requiredDependOnFields}
            disabledDependsOnFields={field?.disabledDependsOnFields}
            justifycontent={field.justifycontent}
            name={parentKey ? `${parentKey}.${field.key}` : field.key}
            setFieldValue={setFieldValue}
            disabled={readOnly ? readOnly : field.disabled}
            disableFutureDates={field.disableFutureDates}
            disablePastDates={field.disablePastDates}
            range={field.range || [1925, 2024]}
            valueKey={field.valueKey}
            fieldKey={field.key}
            info={field.info}
            labelFontWeight={field?.labelFontWeight}
          />
        </>
      )}
      {field.type == 'TIME' && (
        <>
          <TimePickerField
            label={field.label}
            name={parentKey ? `${parentKey}.${field.key}` : field.key}
            setFieldValue={setFieldValue}
            disabled={readOnly}
            labelFontWeight={field?.labelFontWeight}
          />
        </>
      )}
      {field.type == 'DATE_TIME' && (
        <>
          <DateTimePickerField
            label={field.label}
            name={parentKey ? `${parentKey}.${field.key}` : field.key}
            setFieldValue={setFieldValue}
            justifycontent={field.justifycontent}
            disabled={readOnly ? readOnly : field.disabled}
            disableFutureDates={field.disableFutureDates}
            disablePastDates={field.disablePastDates}
            valueKey={field.valueKey}
            fieldKey={field.key}
            labelFontWeight={field?.labelFontWeight}
          />
        </>
      )}
      {field.type == 'SIGNATURE' && (
        <SignatureField
          name={parentKey ? `${parentKey}.${field.key}` : field.key}
          label={field.label}
          label2={field.label2}
          fieldKey={field.key}
          disabled={readOnly || !hasSignatureAccess}
          hasSignAccess={hasSignatureAccess}
          justifycontent={field.justifycontent}
          required={field.required}
          parentKey={parentKey}
          role={field.role}
          showDate={field.showDate}
          siteInitiationMasterId={siteInitiationMasterId}
          setFieldValue={setFieldValue}
          sectionKey={sectionKey}
          {...props}
          labelFontWeight={field?.labelFontWeight}
        />
      )}
      {field.type == 'REMOVE_FIELD_BUTTON' && (
        <Box
          sx={{
            height: props.justifycontent ? 'auto' : '100%',
            display: 'flex',
            pb: 2.65,
            alignItems: field.alignItems ? field.alignItems : 'flex-end',
            justifyContent: field.justifycontent ? 'flex-end' : 'flex-start',
          }}
        >
          <CustomButton
            variant="outlined"
            onClick={() => {
              removeCustomField(field, {
                subSectionKey: props.subSectionKey,
                sectionKey: props.sectionKey,
                categoryKey: parentKey,
              });
            }}
          >
            Remove
          </CustomButton>
        </Box>
      )}
      {field?.fields && (
        <Grid container spacing={isSmallScreen ? 0 : 1}>
          {field.fields.map(
            (childField, childIndex) => {
              const hasChildFieldAccess = !childField.access || 
                (childField.access && (childField.access == userSiteTrialRole || childField.access.includes(userSiteTrialRole)));
              
              // Signature fields are always rendered (shown as disabled if no access), other fields require access
              const shouldRenderChild = checkFieldVisibility(childField, values) && 
                (childField.type === 'SIGNATURE' || hasChildFieldAccess);

              const childOpenQueriesCount = getOpenQueriesCount(childField.key);
              const childHasQueries = childFieldHasQueries(childField.key);
              
              return shouldRenderChild && (
                <Grid
                  key={parentKey ? `${parentKey}.${childIndex}thChild` : `${childField.key}.${childIndex}thChild`}
                  item
                  md={childField.md}
                  lg={childField.lg}
                  sm={childField.sm}
                  xs={childField.sm}
                  sx={{
                    position: 'relative',
                    minWidth: 0,
                    // Highlight fields with open queries
                    // backgroundColor: childOpenQueriesCount > 0 ? 'rgba(255, 152, 0, 0.08)' : 'transparent',
                    backgroundColor: childHasQueries ? 'rgba(255, 152, 0, 0.08)' : 'transparent',
                    borderRadius: childHasQueries ? 1 : 0,
                    border: childOpenQueriesCount > 0 ? `1px solid ${getStatusColor('OPEN').bg}` : 'none',
                    padding: childHasQueries ? '8px' : undefined,
                  }}
                >
                  {childHasQueries && renderQueryIndicator(childField.key)}
                  <DynamicFormField 
                    {...props} 
                    field={childField} 
                    hasSignatureAccess={hasChildFieldAccess} 
                    fieldQueries={getChildFieldQueries(childField.key)}
                  />
                </Grid>
              );
            }
          )}
        </Grid>
      )}
    </Box>
  );
};

export default React.memo(DynamicFormField);
